import GoogleMapWrapped from '@/components/GoogleMap/GoogleMapWrapped';
import GPCalendarEvent from '@/components/GPCalendarEvent';
import GPCardView from '@/components/GPCardView';
import GPTabPanel from '@/components/GPDataViews/GPTabPanel';
import HeaderInlineTab from '@/components/GPDataViews/HeaderInlineTab';
import HeaderTab from '@/components/GPDataViews/HeaderTab';
import { ViewsType, ViewType } from '@/components/GPDataViews/IconTabs';
import GPListView from '@/components/GPListView';
import { useGPFormContext } from '@/contexts/GPFormContext';
import { GroupHomeResponse, GroupSubTabs, PaginationResponse } from '@/requests/types';
import { EventCalendarItem, EventListItem } from '@/types/event';
import { FormSectionType } from '@/types/forms';
import { parseCalViewFromServer } from '@/utils/group';
import { styled, Tab, Tabs } from '@mui/material';
import { GridSize } from '@mui/material/Grid';
import React from 'react';
import { SWRResponse } from 'swr';

type CardSize = {
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
};

const isCardSize = (val: unknown): val is CardSize => {
  if (!val || typeof val !== 'object') return false;

  const allowed = ['xs', 'sm', 'md', 'lg'];

  return Object.keys(val as object).every(key => allowed.includes(key));
};

type MainBaseTabProps<
  T_LR extends string,
  U_LR extends { unique_id?: number | string },
  T_CR extends string,
  U_CR extends { unique_id: string },
  U_MR extends { unique_id: string },
> = {
  tabTitle: string;
  page: number;
  hideTitle: boolean;
  /** Keep the title heading for screen readers but hide it visually (removes redundant on-page header) */
  visuallyHideTitle?: boolean;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  showFilters?: boolean;
  showSearch?: boolean;
  filters?: FormSectionType[];
  viewTabs: ViewsType[];
  subTabs: GroupSubTabs[];
  search?: {
    hint: string;
    onSubmit: () => void;
    noResults?: {
      title?: string;
      subtitle: string;
    };
  };
  view: {
    setCurrentView: React.Dispatch<React.SetStateAction<ViewsType>>;
    currentView: ViewsType;
  };
  list?: {
    response:
      | SWRResponse<PaginationResponse<T_LR, U_LR> | null, unknown>
      | SWRResponse<PaginationResponse<T_LR, U_LR>, unknown>
      | SWRResponse<null, unknown>;
    component: React.ElementType;
    lastItemComponent?: React.ElementType;
    wrapper: T_LR;
    itemKey?: (item: U_LR) => React.Key;
    itemOptions?: Record<string, unknown>;
    gap?: number;
  };
  card?: {
    response:
      | SWRResponse<PaginationResponse<T_CR, U_CR> | null, unknown>
      | SWRResponse<PaginationResponse<T_CR, U_CR>, unknown>
      | SWRResponse<null, unknown>;
    component: React.ElementType;
    lastItemComponent?: React.ElementType;
    wrapper: T_CR;
    size?: unknown; // still flexible upstream
  };
  map?: {
    data?: U_MR[];
    listViewData?: U_LR[];
  };
  calendar?: {
    data?: EventCalendarItem[];
    listViewData?: EventListItem[];
    isValidating?: boolean;
    handleCalDateChange: (start: Date, end: Date) => void;
    defaultView: GroupHomeResponse['calendar_default_view'];
  };
  openOpps?: {
    data?: EventListItem[];
  };
  justifyPagination?: string;
  useInlineHeaderTab?: boolean;
  filterSummary?: React.ReactNode;
  /** Optional content rendered directly above the list (e.g. a totals summary). */
  listHeader?: React.ReactNode;
  /** Optional content rendered below the list (e.g. filter-aware totals). */
  listFooter?: React.ReactNode;
};

const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  textTransform: 'none',
  '&.Mui-selected': {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const MainBaseTab = <
  T_LR extends string,
  U_LR extends { unique_id?: number | string },
  T_CR extends string,
  U_CR extends { unique_id: string },
  U_MR extends { unique_id: string },
>({
  tabTitle,
  page,
  setPage,
  showFilters,
  showSearch,
  filters,
  subTabs,
  viewTabs,
  hideTitle,
  visuallyHideTitle = false,
  search,
  map,
  list,
  card,
  view,
  calendar,
  openOpps,
  justifyPagination,
  useInlineHeaderTab = false,
  filterSummary,
  listHeader,
  listFooter,
}: MainBaseTabProps<T_LR, U_LR, T_CR, U_CR, U_MR>) => {
  const [subTabValue, setSubTabValue] = React.useState(0);

  const activeSubTab = subTabs[subTabValue];
  const tabTitleTemp = activeSubTab?.label ?? tabTitle;

  const handleSubTabChange = (_event: React.SyntheticEvent, nextValue: number) => {
    setSubTabValue(nextValue);
  };

  const { setValue, handleSubmit, onSubmit } = useGPFormContext();

  React.useEffect(() => {
    if (!activeSubTab) return;

    Object.keys(activeSubTab.args).forEach(paramName => {
      setValue(paramName, activeSubTab.args[paramName]);
    });

    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit, setValue, activeSubTab]);

  const HeaderComponent = useInlineHeaderTab ? HeaderInlineTab : HeaderTab;

  const normalizedCardSize: CardSize | undefined = isCardSize(card?.size)
    ? (card.size as CardSize)
    : undefined;

  return (
    <>
      {subTabs.length > 0 && (
        <Tabs onChange={handleSubTabChange} value={subTabValue} sx={{ marginBottom: 1 }}>
          {subTabs.map(navigationItem => (
            <StyledTab label={navigationItem.label} key={navigationItem.unique_id} />
          ))}
        </Tabs>
      )}

      <HeaderComponent
        tabTitle={hideTitle ? '' : tabTitleTemp}
        visuallyHideTitle={visuallyHideTitle}
        setCurrentView={view.setCurrentView}
        currentView={view.currentView}
        searchHint={search?.hint}
        onSubmit={search?.onSubmit}
        showFilters={showFilters}
        showSearch={showSearch}
        viewTabs={viewTabs}
        filters={filters}
      />

      {filterSummary}

      {list?.response ? (
        <GPTabPanel index={ViewType.LIST} value={view.currentView}>
          {listHeader}
          <GPListView
            id="group-list-page"
            response={list.response}
            wrapper={list.wrapper}
            itemKey={list.itemKey}
            pagination={{ page, setPage, justifyContent: justifyPagination }}
            ItemComponent={list.component}
            LastItemComponent={list.lastItemComponent}
            itemHeight={120}
            gap={list.gap}
            noResultsHint={search?.noResults?.subtitle}
            itemOptions={list.itemOptions}
          />
          {listFooter}
        </GPTabPanel>
      ) : null}

      {card?.response && (
        <GPTabPanel index={ViewType.GRID} value={view.currentView}>
          <GPCardView
            id="group-card-page"
            response={card.response}
            wrapper={card.wrapper}
            page={page}
            setPage={setPage}
            ItemComponent={card.component}
            LastItemComponent={card.lastItemComponent}
            itemHeight={120}
            noResultsHint={search?.noResults?.subtitle}
            size={normalizedCardSize}
          />
        </GPTabPanel>
      )}

      {map && (
        <GPTabPanel index={ViewType.MAP} value={view.currentView}>
          <GoogleMapWrapped {...map} disableResearchButton />
        </GPTabPanel>
      )}

      {calendar && (
        <GPTabPanel index={ViewType.CALENDAR} value={view.currentView}>
          <GPCalendarEvent
            events={calendar.data}
            openEvents={openOpps?.data}
            isValidating={calendar.isValidating}
            onDateChange={calendar.handleCalDateChange}
            defaultView={parseCalViewFromServer(calendar.defaultView)}
          />
        </GPTabPanel>
      )}
    </>
  );
};

export default MainBaseTab;
